import { sortBy, remove } from 'lodash-es';
import { from, Observable } from 'rxjs';
import AdminService from 'external-services/diary-admin-service/admin.service';
import NetworkConnectionService from 'external-services/network-connection-service';
import {
  IBooking,
  IBookingTag,
  IBookingTagType,
  ISavedBookingSelectedOptions,
  ISavedExtras,
  servicePaymentType,
  AdminServiceLog,
  PouchDocType,
  VenueActionNames,
  ITrackingService,
} from 'shared-types/index';
import {
  ITagDocumentRow,
  ITagTypeRow,
  ITagColour,
  ITagIconResponse,
  ITagTypes,
  ITagDocument,
  IInitTagsService,
  ITagHeading,
} from './types';
import { IAuthService } from 'external-services/diary-admin-service/types';
import { hardCodedTags as globalHardCodedTags, sharedTagIds } from './constants';
import { cloneDeep, uniqBy } from 'lodash-es';
import PouchLoadService from 'external-services/pouch-load-service';

import { AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service';

export * from './constants';

const NS = 'TagsService';

class TagsService {
  private TrackingService: ITrackingService;

  private tagsCache: IBookingTag[] = null;
  private sharedTagsCache: IBookingTag[] = null;
  private tagLookUp: any;
  private api: AxiosInstance;

  private getVenueId: () => number;
  private getIsManager: () => boolean;

  public _setupForUnitTest(api: AxiosInstance): void {
    this.api = api;
  }

  public noShowTag: IBookingTag = {
    _id: sharedTagIds.noShow,
    name: 'Previous No Show',
    tagtype: {
      name: 'Other',
      type: 'tagtype',
      id: globalHardCodedTags.OTHER_TAG_TYPE,
    },
    colour: '#b51b00',
    permanent: true,
    type: 'tag',
    icon: 'no-show',
    online: false,
  };
  public nowShowTagShared: IBookingTag = {
    _id: sharedTagIds.noShow,
    name: 'Previous No Show',
    tagtype: {
      name: 'Shared',
      _id: globalHardCodedTags.SHARED_TAG_TYPE,
      type: 'tagtype',
    },
    colour: '#b51b00',
    permanent: true,
    type: 'tag',
    icon: 'no-show',
    online: false,
    editable: false,
  };
  public vipTag: IBookingTag = {
    _id: sharedTagIds.vip,
    name: 'VIP',
    tagtype: {
      name: 'VIP',
      type: 'tagtype',
      id: 'tagtype_vip',
    },
    permanent: true,
    type: 'tag',
    icon: 'vip',
    online: false,
    editable: false,
  };
  public tagColours: ITagColour[] = [
    {
      name: 'Black',
      value: '#000000',
    },
    {
      name: 'Dark Red',
      value: '#b51b00',
    },
    {
      name: 'Purple',
      value: '#9220d7',
    },
    {
      name: 'Orange',
      value: '#D79A20',
    },
    {
      name: 'Green',
      value: '#2CB90D',
    },
    {
      name: 'Blue',
      value: '#206ED7',
    },
    {
      name: 'Red',
      value: '#ff0030',
    },
    {
      name: 'Bright Green',
      value: '#0ae228',
    },
    {
      name: 'Dark Green',
      value: '#267347',
    },
    {
      name: 'Grey',
      value: '#DFDFDF'
    }
  ];

  public sharedTagHeading: ITagHeading = {
    name: 'Admin Shared Tags',
    tagtype: {
      name: 'tags_heading',
      type: 'tagtype',
      _id: 'tags_heading',
    },
  };

  public diaryTagHeading: ITagHeading = {
    name: 'Diary Tags',
    tagtype: {
      name: 'tags_heading',
      type: 'tagtype',
      _id: 'tags_heading',
    },
  };

  private bookingAttributeTags = {
    hasNote: {
      _id: 'attribute_tag_hasNote',
      icon: 'notes',
      name: 'Notes',
    },
    hasVisitedMultipleVenues: {
      _id: 'attribute_tag_hasVisitedMultipleVenues',
      icon: 'cards-diamond',
      name: 'Visited other venues in this account',
    },
    walkin: {
      _id: 'attribute_tag_walkin',
      icon: 'walkin',
      name: 'Walk in',
    },
    locked: {
      _id: 'attribute_tag_locked',
      icon: 'locked',
      name: 'Locked',
    },
    preAuth: {
      _id: 'attribute_tag_preAuth',
      icon: 'pre-auth',
      name: 'Pre-auth',
    },
    payment: {
      _id: 'attribute_tag_payment',
      icon: 'payment',
      name: 'Payment',
    },
  };

  public init(props: IInitTagsService): void {
    const {
      TrackingService,
      PouchActions,
      VenueActions,
      getVenueId,
      getIsManager,
    } = props;
    this.TrackingService = TrackingService;
    this.getVenueId = getVenueId;
    this.getIsManager = getIsManager;
    this.api = NetworkRequestsService.getGlobalAPI();
    PouchActions.handlePouchSyncedDoctype(PouchDocType.tag, () => this.deleteCache());
    VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => this.deleteCache());
    VenueActions.handleVenueAction(VenueActionNames.VENUE_LOADED, () => this.deleteCache());
  }

  /**
   * This function is only for unit-test
   * @param tagsCache
   */
  public testTagsCache(tagsCache: IBookingTag[]): void {
    this.tagsCache = tagsCache;
  }

  private getTagsArrayPromise : Promise<IBookingTag[]> = null;
  public getTags(reload: boolean = null): Promise<IBookingTag[]> {
    return new Promise((resolve, reject) => {
      if (this.tagsCache && !reload) {
        resolve(this.tagsCache);
      } else {
        Promise.resolve(
          PouchLoadService.getDbLocal()
            .allDocs({
              include_docs: true,
              attachments: false,
              startkey: 'tag_',
              endkey: 'tag_\uffff',
            })
            .then((tagDocuments: unknown) => {
              const tags = (tagDocuments as ITagDocument).rows.map((item: ITagDocumentRow) => {
                return item.doc;
              });

              //Correct invalid tagType if anny
              this.updateTagType(tags);
              return tags;
            })
            .then((venueTags: IBookingTag[]) => {
              const orderedVenueTags: IBookingTag[] = sortBy(venueTags, 'order');

              // Note: there used to be a check for isOnlineCustomersEnabled, but this is always assumed to be true now, so condition has been removed

              // check if there is shared tags cache and if it needs a reload
              if (this.sharedTagsCache && !reload) {
                this.tagsCache = this.sharedTagsCache.concat(orderedVenueTags);
                this.addDeliveryFeeTag(this.tagsCache);
                resolve(this.tagsCache);
              } else {
                // if online load shared tags from server, otherwise resolve what we have
                //Check if we are already updating the cache, only allow one request in flight at once
                if(this.getTagsArrayPromise) {
                  //there's already a request happening, we might as well also use it to resolve this promise
                  this.getTagsArrayPromise.then((res) => resolve(res));
                  return;
                }
                this.getTagsArrayPromise = this.getTagsArray(orderedVenueTags);
                this.getTagsArrayPromise.then((res: IBookingTag[]) => {
                  this.getTagsArrayPromise = null;
                  resolve(res);
                });
              }
            })
        );
      }
    });
  }

  private updateTagType(tags: any[]) {
    try {
      if (!tags || tags.length == 0) return;
      const tagsToUpdate: any[] = [];

      tags.forEach((tag) => {
        //This is to fix "T" property that cause the tags can't load
        if (tag['tagType']) {
          tag['tagtype'] = tag['tagType'];
          delete tag.tagType;
          tagsToUpdate.push(tag);
        }
      });

      if (tagsToUpdate.length > 0) {
        tagsToUpdate.forEach((tag) => {
          this.updateTag(tag);
          AdminService.serverLoggingDbEvents(
            this.getVenueId().toString(),
            `Updated Tag ${tag.name}(${tag.tagtype.name})`,
            AdminServiceLog.INFOLOG,
            ''
          );
        });
      }

      /**
       * Note: there used to be an isOnlineCustomersEnabled check here, but it was removed as isOnlineCustomersEnabled is
       * now always assumed to be true
       */
      //Delete no-show, VIP and membership in couch as
      const noShowTags = tags.filter((t) => t._id == sharedTagIds.noShow);
      noShowTags.forEach((t) => {
        this.deleteTag(t);
        AdminService.serverLoggingDbEvents(
          this.getVenueId().toString(),
          `Delete local couch shared tag ${t.name}`,
          AdminServiceLog.INFOLOG,
          ''
        );
      });

      const noMemberships = tags.filter((t) => t._id == sharedTagIds.membership);
      noMemberships.forEach((t) => {
        this.deleteTag(t);
        AdminService.serverLoggingDbEvents(
          this.getVenueId().toString(),
          `Delete local couch shared tag ${t.name}`,
          AdminServiceLog.INFOLOG,
          ''
        );
      });

      const noVips = tags.filter((t) => t._id == sharedTagIds.vip);
      noVips.forEach((t) => {
        this.deleteTag(t);
        AdminService.serverLoggingDbEvents(
          this.getVenueId().toString(),
          `Delete local couch shared tag ${t.name}`,
          AdminServiceLog.INFOLOG,
          ''
        );
      });
    } catch (ex) {}
  }

  /**
   * Adds the delivery fee tag if it doesn't already exist.
   * We check for manually created 'delivery-fee' (name) tags as well as 'delivery_fee' (id) tags
   * because originally (in the COVID-19 rush for takeaways) we added this tag manually (using name),
   * then later added it by default (using id, which is better), so we must now support both name and id.
   */
  public addDeliveryFeeTag(arr: IBookingTag[]): void {
    /**
     * if tag already exists, ignore this function
     * @todo remove the below 'check by name' once all early 'delivery-fee' venues have removed their
     * manually created tags and started using the 'by id' delivery tag.
     */
    if (
      arr.find((t: IBookingTag) => t.name === globalHardCodedTags.DELIVERY_TAG_NAME) ||
      arr.find((t: IBookingTag) => t._id === globalHardCodedTags.DELIVERY_TAG_ID)
    ) {
      return;
    }
    arr.unshift({
      _id: globalHardCodedTags.DELIVERY_TAG_ID,
      name: 'Delivery Fee',
      tagtype: {
        name: 'Other',
        type: 'tagtype',
        id: globalHardCodedTags.OTHER_TAG_TYPE,
      },
      colour: '#2CB90D', // green
      permanent: false,
      noneditable: true, // only option with this field
      type: 'tag',
      icon: 'cash',
      online: true,
    });
  }

  public addBookedByTag(tagArray: IBookingTag[]): void {
    if (tagArray.find((t: IBookingTag) => t._id === globalHardCodedTags.BOOKEDBY_TAG_ID)) {
      return;
    }

    tagArray.push({
      _id: globalHardCodedTags.BOOKEDBY_TAG_ID,
      name: globalHardCodedTags.BOOKEDBY_TAG_NAME,
      icon: 'booked_by',
      online: false,
      permanent: false,
      noneditable: true,
      editable: false,
      type: 'tag',
      tagtype: {
        id: globalHardCodedTags.BOOKEDBY_TAG_ID,
        name: 'Booked by',
        type: 'tagtype',
      }
    });
  }

  public removeBookedByTag(tagArray: IBookingTag[]): void {
    remove(tagArray, t => t._id == globalHardCodedTags.BOOKEDBY_TAG_ID);
  }

  public getTagById(id: string): Promise<IBookingTag> {
    return new Promise((resolve, reject) => {
      this.getTags().then((tags: IBookingTag[]) => {
        const tag: IBookingTag = tags.find((t: IBookingTag) => {
          return t._id === id;
        });
        if (tag) {
          resolve(tag);
        } else {
          reject();
        }
      });
    });
  }

  public addTag(tag: IBookingTag): Promise<PouchDB.Core.Response> {
    this.deleteCache();
    if (!tag.type) {
      tag.type = 'tag';
    }
    tag._id = PouchLoadService.getNewId(['tag']);
    this.TrackingService.trackEvent('New Tag', {
      id: tag._id,
    });
    return Promise.resolve(PouchLoadService.getDbLocal().put(tag));
  }

  public updateTag(tag: IBookingTag): Promise<PouchDB.Core.Response> {
    this.deleteCache();
    if (!tag.type) {
      tag.type = 'tag';
    }
    this.TrackingService.trackEvent('Edit Tag', {
      id: tag._id,
      rev: tag._rev,
    });
    if (tag._id === 'tag_no_show') {
      this.noShowTag = tag;
    }
    return Promise.resolve(PouchLoadService.getDbLocal().put(tag));
  }

  public filterTagsBeforeSavingToApi(booking: IBooking): void {
    const arrBookingAttributeTags = Object.values(this.bookingAttributeTags);

    booking.tags = booking.tags.filter((tag) => {
      const existingTag = arrBookingAttributeTags.find((v) => v._id === tag._id)

      return !existingTag
    })
  }

  public deleteTag(tag: IBookingTag): Promise<PouchDB.Core.Response> {
    this.deleteCache();
    this.TrackingService.trackEvent('Delete Tag', {
      id: tag._id,
      rev: tag._rev,
    });
    return Promise.resolve(
      PouchLoadService.getDbLocal().remove(tag as PouchDB.Core.RemoveDocument)
    );
  }

  public saveTags(tags: IBookingTag[]): Promise<(PouchDB.Core.Response | PouchDB.Core.Error)[]> {
    this.TrackingService.trackEvent('Save Tags');
    return Promise.resolve(PouchLoadService.getDbLocal().bulkDocs(tags));
  }

  public async getTagIcons(): Promise<ITagIconResponse[]> {
    const response = await this.api.get('json/tagIcons.json');
    return response.data;
  }

  public getTagTypes(): Promise<IBookingTagType[]> {
    return Promise.resolve(
      PouchLoadService.getDbLocal()
        .allDocs({
          include_docs: true,
          attachments: false,
          startkey: 'tagtype_',
          endkey: 'tagtype_\uffff',
        })
        .then((tagTypes: unknown) => {
          return (tagTypes as ITagTypes).rows.map((item: ITagTypeRow) => {
            return item.doc;
          });
        })
    );
  }

  public addTypeToTags(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.getTags()
        .then((tags: IBookingTag[]) => {
          const tagsToUpdate = [];
          for (let i = tags.length - 1; i >= 0; i--) {
            const tag = tags[i];
            if (!tag.type) {
              tag.type = 'tag';
              tagsToUpdate.push(tag);
            }
          }
          return tagsToUpdate;
        })
        .then((tagsToUpdate: IBookingTag[]) => {
          if (tagsToUpdate.length) {
            this.saveTags(tagsToUpdate).then(() => {
              resolve();
            });
          } else {
            resolve();
          }
        })
        .catch(function () {
          resolve();
        });
    });
  }

  public getAdditionalTagIcons(
    booking: IBooking,
    tags: IBookingTag[],
    useCache: boolean = null
  ): { icon: string; name: string }[] {
    // create tag object look up
    if (!this.tagLookUp || !useCache) {
      this.tagLookUp = {};
      for (const tag of tags) {
        this.tagLookUp[tag._id] = tag;
      }
    }

    // Add attribute tags
    const tagIcons: { icon: string; name: string; _id: string }[] = [];
    if (
      (booking.notes && booking.notes.trim().length != 0) ||
      booking.hasCustomerNotes ||
      (booking.hasManagerNotes && this.getIsManager())
    ) {
      tagIcons.push({ ...this.bookingAttributeTags.hasNote });
    }

    if (booking.hasVisitedMultipleVenues) {
      tagIcons.push({ ...this.bookingAttributeTags.hasVisitedMultipleVenues });
    }

    if (booking.method === 'walkin') {
      tagIcons.push({ ...this.bookingAttributeTags.walkin });
    }

    if (booking.locked) {
      tagIcons.push({ ...this.bookingAttributeTags.locked });
    }

     // only for paid preAuth bookings we need to show the icon - NBI-8790
     if (booking.paymentSummary && booking.paymentSummary.paymentType === servicePaymentType.preAuth) {
      tagIcons.push({ ...this.bookingAttributeTags.preAuth });
    } else if (
      booking.paymentSummary &&
      booking.paymentSummary.transactionId !== 0 &&
      booking.paymentSummary.transactionId !== null &&
      booking.paymentSummary.paymentType !== servicePaymentType.noPayment
    ) {
      tagIcons.push({ ...this.bookingAttributeTags.payment });
    }

    // Merge attribute tags with tags
    if (booking.tags) {
      const orderedTags: IBookingTag[] = sortBy(booking.tags, ['order']);
      /**
       * Verified icon must appear before all the tags added by the venue. i.e. Dietary, Accessibility, etc.
       * But, it should appear after system generated icons. i.e. Notes, Walkin, Payment, etc.
       */
      const sortedList: IBookingTag[] = orderedTags.sort((a: IBookingTag, b: IBookingTag) =>
        this.compareTagIcons(a.name, b.name)
      );
      sortedList.forEach((ot: IBookingTag) => {
        const matchedTag = tagIcons.find((ti) => ti._id === ot._id);
        if (!matchedTag) {
          tagIcons.push(this.tagLookUp[ot._id] ? this.tagLookUp[ot._id] : ot);
        }
      });
    }

    return tagIcons;
  }

  // observable wrapper for getBookingTags, so it is cancellable
  public getBookingTags$(
    booking: IBooking,
    useCache: boolean = false
  ): Observable<{ icon: string; name: string }[]> {
    return from(this.getBookingTags(booking, useCache));
  }

  public getBookingTags(
    booking: IBooking,
    useCache: boolean = false
  ): Promise<{ icon: string; name: string }[]> {
    if (!booking) {
      return Promise.resolve([]);
    }

    if (useCache && this.tagsCache) {
      return Promise.resolve(this.getAdditionalTagIcons(booking, this.tagsCache, useCache));
    }

    return new Promise((resolve) => {
      this.getTags().then((tags: any) => {
        resolve(this.getAdditionalTagIcons(booking, tags));
      });
    });
  }

  // removed 'checkNoShow', as isOnlineCustomersEnabled is now always assumed to be true

  public updateVIPTag(): void {
    this.getTags().then((tags: IBookingTag[]) => {
      const vipTag = tags.find((tag: IBookingTag) => {
        return (
          tag.name === 'VIP' &&
          tag.tagtype &&
          tag.tagtype._id !== globalHardCodedTags.SHARED_TAG_TYPE
        );
      });
      if (vipTag) {
        // Update only if not already updated
        if (vipTag.permanent !== true || vipTag.editable !== false) {
          vipTag.permanent = true;
          vipTag.editable = false;
          PouchLoadService.getDbLocal().put(vipTag);
        }
      }
    });
  }

  public checkPermanentTags(
    customerTags: IBookingTag[],
    bookingTags: IBookingTag[]
  ): IBookingTag[] {
    const temp: IBookingTag[] = bookingTags ? [...bookingTags] : [];
    let isChange: boolean = false; // check to need to update booking tags.
    [...customerTags].reverse().forEach((tag: IBookingTag) => {
      if (tag.permanent) {
        // check for tagType as well as tagtype because there is a lot of inconsistent data
        if (tag && tag !== undefined && (tag.tagtype || (tag as any).tagType)) {
          if (!bookingTags) {
            isChange = true;
          }
          const index: number = bookingTags
            .map((item: IBookingTag) => {
              return item._id;
            })
            .indexOf(tag._id);
          // make sure the tag is not already in the array
          if (index === -1 && tag) {
            const tagtype = { name: tag.tagtype.name, _id: tag.tagtype._id };
            const simpleTag: IBookingTag = {
              colour: tag.colour,
              editable: tag.editable,
              icon: tag.icon,
              name: tag.name,
              noneditable: tag.noneditable,
              online: tag.online,
              order: tag.order,
              permanent: tag.permanent,
              tagtype: tagtype,
              type: tag.type,
              _id: tag._id,
            } as IBookingTag;
            temp.push(simpleTag);
            isChange = true;
          }
        }
      }
    });
    return isChange ? temp : null;
  }

  // ################################## Private methods ##############################
  private deleteCache(): void {
    this.tagsCache = null;
  }

  private getTagsArray(orderedVenueTags: IBookingTag[]): Promise<IBookingTag[]> {
    if (NetworkConnectionService.isOnline()) {
      return new Promise((resolve) => {
        this.api
          .get(
            AdminService.getSharedTagsEndpointsURL()
          )
          .then((response: { data: IBookingTag[] }) => {
            this.sharedTagsCache = response.data;
          })
          .catch((error) => {
            console.error('Load shared tags', error);
          })
          .finally(() => {
            this.tagsCache = this.sharedTagsCache
              ? this.sharedTagsCache.concat(orderedVenueTags)
              : orderedVenueTags;
            this.addDeliveryFeeTag(this.tagsCache);
            resolve(this.tagsCache);
          });
      });
    } else {
      this.tagsCache = this.sharedTagsCache
        ? this.sharedTagsCache.concat(orderedVenueTags)
        : orderedVenueTags;
      this.addDeliveryFeeTag(this.tagsCache);
      return Promise.resolve(this.tagsCache);
    }
  }

  /*
  This method gets the array of tags back from updateTagsFromSelectedMenuOptions and combines
  them with the existing tags present in booking.tags. It then removes the duplicates.
  and return the final Tag Array. Return type is IBookingTag[]
   */

  public mergeAndGetUniqueTags(
    selectedOptions: ISavedBookingSelectedOptions[],
    allTags: IBookingTag[],
    existingTags: IBookingTag[]
  ): IBookingTag[] {
    if (selectedOptions && selectedOptions.length === 0) {
      return existingTags;
    }
    selectedOptions = selectedOptions.filter(
      (item: ISavedBookingSelectedOptions) => item.quantity > 0
    );
    const selectedOptionTags: IBookingTag[] = this.updateTagsFromSelectedMenuOptions(
      cloneDeep(selectedOptions),
      allTags
    );
    const combinedTags: IBookingTag[] = [...existingTags, ...selectedOptionTags];
    return uniqBy(combinedTags, '_id');
  }

  public searchTags(query: string, allTags: IBookingTag[]): (IBookingTag | ITagHeading)[] {
    const lowercaseQuery = query ? query.toLowerCase() : query;

    const results = query
      ? allTags.filter((tag) => {
          if (tag && tag.name && tag.name.toLowerCase().indexOf(lowercaseQuery) === 0) {
            return true;
          }
          if (
            tag &&
            tag.tagtype &&
            tag.tagtype.name &&
            tag.tagtype.name.toLowerCase().indexOf(lowercaseQuery) === 0
          ) {
            return true;
          }
          return false;
        })
      : allTags;

    const sharedTags = results.filter((t) => t.tagtype._id == globalHardCodedTags.SHARED_TAG_TYPE);
    const diaryTags = results.filter((t) => t.tagtype._id != globalHardCodedTags.SHARED_TAG_TYPE);
    const resultWithHeading = [];

    if (sharedTags.length > 0) {
      resultWithHeading.push(this.sharedTagHeading);
      resultWithHeading.push(...sharedTags);
    }

    if (diaryTags.length > 0) {
      resultWithHeading.push(this.diaryTagHeading);
      resultWithHeading.push(...diaryTags);
    }

    return resultWithHeading;
  }

  /**
   * Create filter function for a query string
   */

  /*
  This method calculates all the tags from the BO and their extras
  and return a array of Tags back. Return type is IBookingTag[]
   */

  private updateTagsFromSelectedMenuOptions(
    selectedOptions: ISavedBookingSelectedOptions[],
    allTags: IBookingTag[]
  ): IBookingTag[] {
    const newTags: IBookingTag[] = [];
    (selectedOptions || []).forEach((selectedOption) => {
      if (selectedOption.extras && selectedOption.extras.length > 0) {
        selectedOption.extras.forEach((extra: ISavedExtras) => {
          extra.tag = extra.tag ? allTags.find((tag: IBookingTag) => tag._id === extra.tag) : null;
          newTags.push(extra.tag);
        });
      }
      selectedOption.tag = selectedOption.tag
        ? allTags.find((tag: IBookingTag) => tag._id === selectedOption.tag)
        : null;
      newTags.push(selectedOption.tag);
    });
    return newTags.filter(Boolean);
  }

  /**
   * Use this method to sort tag icons in ascending order i.e. ["Dietary", "Occasion", "Verified"].sort(compareTagIcons)
   * Verified tag appears at the top of the list.
   * 1. If the return value < than 0, then it sorts firstElement before secondElement.
   * e.g. If firstElement='Dietary' and secondElement='Occasion', then Dietary is placed before Occasion.
   * 2. If the return value > than 0, then it sorts secondElement before firstElement.
   * e.g. If firstElement='Occasion' and secondElement='Dietary', then Occasion is placed before Dietary.
   * 3. If the return value = 0, then firstElement and secondElement are considered equal.
   * e.g. If firstElement='Dietary' and secondElement='Dietary', then no change take place.
   */
  public compareTagIcons(firstElement: string, secondElement: string): number {
    if (firstElement === 'Verified') return -1;

    if (secondElement === 'Verified') return 1;

    if (firstElement < secondElement) return -1;

    if (firstElement > secondElement) return 1;

    return 0;
  }
}

const instance = new TagsService();
export default instance as TagsService;
